import { priceIntelMultipartRequest, priceIntelRequest } from './api-client';
import {
  approveLocalCandidate,
  classifyLocalIntent,
  clearLocalPriceIntelData,
  commitLocalParsedPreview,
  createLocalBriefing,
  createLocalManualCandidate,
  createLocalSourceFromBrowserFile,
  createLocalSourceFromText,
  fetchLocalBriefings,
  fetchLocalCandidates,
  fetchLocalJobs,
  fetchLocalPriceFacts,
  fetchLocalTrends,
  parseLocalSourcePreview,
  readLocalInputText,
  rejectLocalCandidate,
  seedLocalPriceIntelData,
  updateLocalCandidate,
} from './demo-price-intel-service';
import { getPriceIntelRuntimeMode } from './price-intel-mode';
import type {
  PriceIntelBriefing,
  PriceIntelCandidateFact,
  PriceIntelCandidateUpdateInput,
  PriceIntelIngestionJob,
  PriceIntelManualCandidateInput,
  PriceIntelPreviewResult,
  PriceIntelPriceFact,
  PriceIntelSourceCreateResponse,
  PriceIntelTrendResponse,
} from '../types';

type CreateSourceFromFileOptions = {
  file?: File;
  fileName?: string;
  storageKey?: string;
  metadata?: Record<string, unknown>;
  folder?: string;
};

export type CreateSourceFromTextPayload = {
  title?: string;
  input_text: string;
  metadata?: Record<string, unknown>;
};

export function isLocalDemoMode(): boolean {
  return getPriceIntelRuntimeMode() === 'local';
}

type PresignUploadResponse = {
  key: string;
  upload_url: string;
  public_url?: string | null;
};

async function createRemoteSourceRecord(payload: {
  source_kind: 'file' | 'text';
  source_name: string;
  mime_type?: string;
  file_storage_key?: string;
  input_text?: string;
  metadata?: Record<string, unknown>;
  create_job?: boolean;
}): Promise<PriceIntelSourceCreateResponse> {
  return priceIntelRequest<PriceIntelSourceCreateResponse>('/api/v1/price_intel/sources', {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      create_job: payload.create_job ?? true,
      metadata: payload.metadata || {},
    }),
  });
}

async function requestRemoteUploadPresign(file: File, folder = 'price_intel/uploads'): Promise<PresignUploadResponse> {
  return priceIntelRequest<PresignUploadResponse>('/api/v1/price_intel/uploads/presign', {
    method: 'POST',
    body: JSON.stringify({
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
      folder,
      expires_in: 3600,
    }),
  });
}

async function uploadFileToPresignedUrl(file: File, uploadUrl: string): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Upload failed with ${response.status}`);
  }
}

export async function createSourceFromUpload(
  file: File,
  options?: CreateSourceFromFileOptions,
): Promise<PriceIntelSourceCreateResponse> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return createLocalSourceFromBrowserFile({
      file,
      sourceName: options?.fileName || file.name,
      storageKey: options?.storageKey,
      metadata: options?.metadata,
    });
  }

  const presign = await requestRemoteUploadPresign(file, options?.folder);
  await uploadFileToPresignedUrl(file, presign.upload_url);

  return createRemoteSourceRecord({
    source_kind: 'file',
    source_name: options?.fileName || file.name,
    mime_type: file.type || 'application/octet-stream',
    file_storage_key: presign.key,
    metadata: options?.metadata,
  });
}

export async function createSourceFromFile(
  fileId: string,
  options?: CreateSourceFromFileOptions,
): Promise<PriceIntelSourceCreateResponse> {
  if (getPriceIntelRuntimeMode() === 'local') {
    if (!options?.file) {
      throw new Error('本地模式需要浏览器里的文件对象才能解析。');
    }
    return createLocalSourceFromBrowserFile({
      fileId,
      file: options.file,
      sourceName: options.fileName,
      storageKey: options.storageKey,
      metadata: options.metadata,
    });
  }

  return priceIntelRequest<PriceIntelSourceCreateResponse>('/api/v1/price_intel/sources/from-file', {
    method: 'POST',
    body: JSON.stringify({
      file_id: fileId,
      metadata: options?.metadata || {},
    }),
  });
}

export async function createSourceFromText(payload: CreateSourceFromTextPayload): Promise<PriceIntelSourceCreateResponse> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return createLocalSourceFromText(payload);
  }

  return createRemoteSourceRecord({
    source_kind: 'text',
    source_name: payload.title || 'Manual text source',
    mime_type: 'text/plain',
    input_text: payload.input_text,
    metadata: payload.metadata,
  });
}

export async function fetchJobs(status?: string): Promise<PriceIntelIngestionJob[]> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return fetchLocalJobs(status);
  }
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  return priceIntelRequest<PriceIntelIngestionJob[]>(`/api/v1/price_intel/jobs${query}`);
}

export async function fetchCandidates(reviewStatus = 'pending_review'): Promise<PriceIntelCandidateFact[]> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return fetchLocalCandidates(reviewStatus);
  }
  return priceIntelRequest<PriceIntelCandidateFact[]>(
    `/api/v1/price_intel/reviews/candidates?review_status=${encodeURIComponent(reviewStatus)}`,
  );
}

export async function updateCandidate(
  candidateFactId: string,
  payload: PriceIntelCandidateUpdateInput,
): Promise<PriceIntelCandidateFact> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return updateLocalCandidate(candidateFactId, payload);
  }
  return priceIntelRequest<PriceIntelCandidateFact>(`/api/v1/price_intel/reviews/candidates/${candidateFactId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function approveCandidate(candidateFactId: string): Promise<{ price_fact_id: string }> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return approveLocalCandidate(candidateFactId);
  }
  return priceIntelRequest<{ price_fact_id: string }>(`/api/v1/price_intel/reviews/candidates/${candidateFactId}/approve`, {
    method: 'POST',
  });
}

export async function rejectCandidate(candidateFactId: string, reviewComment?: string): Promise<PriceIntelCandidateFact> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return rejectLocalCandidate(candidateFactId, reviewComment);
  }
  return priceIntelRequest<PriceIntelCandidateFact>(`/api/v1/price_intel/reviews/candidates/${candidateFactId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ review_comment: reviewComment }),
  });
}

export async function createManualCandidate(
  payload: PriceIntelManualCandidateInput,
): Promise<PriceIntelCandidateFact> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return createLocalManualCandidate(payload);
  }
  return priceIntelRequest<PriceIntelCandidateFact>('/api/v1/price_intel/reviews/candidates/manual', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchPriceFacts(filters?: {
  competitor?: string;
  region?: string;
  country?: string;
  year_quarter?: string;
}): Promise<PriceIntelPriceFact[]> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return fetchLocalPriceFacts(filters);
  }
  const search = new URLSearchParams();
  if (filters?.competitor) search.set('competitor', filters.competitor);
  if (filters?.region) search.set('region', filters.region);
  if (filters?.country) search.set('country', filters.country);
  if (filters?.year_quarter) search.set('year_quarter', filters.year_quarter);
  const query = search.toString() ? `?${search.toString()}` : '';
  return priceIntelRequest<PriceIntelPriceFact[]>(`/api/v1/price_intel/facts${query}`);
}

export async function fetchTrends(filters?: {
  competitor?: string;
  region?: string;
  country?: string;
}): Promise<PriceIntelTrendResponse> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return fetchLocalTrends(filters);
  }
  const search = new URLSearchParams();
  if (filters?.competitor) search.set('competitor', filters.competitor);
  if (filters?.region) search.set('region', filters.region);
  if (filters?.country) search.set('country', filters.country);
  const query = search.toString() ? `?${search.toString()}` : '';
  return priceIntelRequest<PriceIntelTrendResponse>(`/api/v1/price_intel/facts/trends${query}`);
}

export async function fetchBriefings(): Promise<PriceIntelBriefing[]> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return fetchLocalBriefings();
  }
  return priceIntelRequest<PriceIntelBriefing[]>('/api/v1/price_intel/briefings');
}

export async function createBriefing(payload: {
  title: string;
  reporting_period?: string;
  competitor?: string;
  region?: string;
  country?: string;
}): Promise<PriceIntelBriefing> {
  if (getPriceIntelRuntimeMode() === 'local') {
    return createLocalBriefing(payload);
  }
  return priceIntelRequest<PriceIntelBriefing>('/api/v1/price_intel/briefings', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function seedDemoData(): Promise<void> {
  if (getPriceIntelRuntimeMode() === 'local') {
    await seedLocalPriceIntelData();
  }
}

export async function clearDemoData(): Promise<void> {
  if (getPriceIntelRuntimeMode() === 'local') {
    await clearLocalPriceIntelData();
  }
}

// =============================================================================
// 真·AI 解析工作台（Live Parse Workbench）
// -----------------------------------------------------------------------------
// 所有模式都调后端 `POST /api/v1/price_intel/parse/preview-*`（无鉴权、同步调 LLM）。
// 本地模式下 /commit-preview 不调用后端，而是复用 demo 的内存落库；
// 在线模式下 /commit-preview 写真 DB，走审核台标准流程。
// =============================================================================

export interface ParsePreviewCandidate {
  competitor?: string | null;
  series?: string | null;
  model?: string | null;
  pitch_mm?: number | null;
  brightness?: number | null;
  application?: string | null;
  indoor_outdoor?: string | null;
  region?: string | null;
  country?: string | null;
  price_per_piece?: number | null;
  price_per_sqm?: number | null;
  currency?: string | null;
  year_quarter?: string | null;
  notes?: string | null;
  confidence?: number | null;
  source_excerpt?: string | null;
  source_page?: number | null;
  competitor_id?: string | null;
  product_model_id?: string | null;
  normalized_payload?: Record<string, unknown>;
}

export interface ParsePreviewResult {
  sourceName: string;
  sourceKind: 'file' | 'text';
  mimeType: string | null;
  pipeline: string[];
  contentType: string;
  extractedTextExcerpt: string;
  extractedTextLength: number;
  model: string | null;
  log: string[];
  candidates: ParsePreviewCandidate[];
  /** 前端暂存的输入信息，commit 时需要一并写入 source_record。 */
  _upload?: {
    file?: File;
    inputText?: string;
    metadata?: Record<string, unknown>;
    fileStorageKey?: string;
  };
}

export interface ParsePreviewInput {
  kind: 'file' | 'text';
  file?: File;
  sourceName?: string;
  inputText?: string;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// 采集意图层（E1/E2/E3）：先识别文档类型 → 用户确认 → 定向抽取
// =============================================================================

export type DocIntent = 'price_quote' | 'spec_sheet' | 'tender_checklist' | 'research_note' | 'general';

export interface DocFingerprint {
  char_count: number;
  table_count: number;
  paragraph_count: number;
  price_signal_count: number;
  spec_signal_count: number;
  competitor_mentions: string[];
  region_mentions: string[];
  quarter_mentions: string[];
  has_price_table: boolean;
  dominant_block_type: string;
}

export interface IntentVerdict {
  intent: DocIntent;
  confidence: number;
  intent_source: 'rule' | 'llm' | 'user_override';
  reason: string;
  suggested_fields: string[];
  alternatives: string[];
  needs_user_confirm: boolean;
}

export interface ParseIntentResult {
  sourceName: string;
  sourceKind: 'file' | 'text';
  mimeType: string | null;
  pipeline: string[];
  contentType: string;
  fingerprint: DocFingerprint;
  intent: IntentVerdict;
  extractedTextExcerpt: string;
  extractedTextLength: number;
  /** 文档中枢本地持久化 ID（后端已落库时返回） */
  docId?: string | null;
  aiSummary?: {
    headline: string;
    key_points: string[];
    suggested_followups?: string[];
    summary_source?: string;
  };
}

/** 后端 PreviewIntentResponse 的原始结构（snake_case）。 */
interface BackendPreviewIntentResponse {
  source_name: string;
  source_kind: 'file' | 'text';
  mime_type: string | null;
  pipeline: string[];
  content_type: string;
  fingerprint: DocFingerprint;
  intent: IntentVerdict;
  extracted_text_excerpt: string;
  extracted_text_length: number;
  doc_id?: string | null;
  ai_summary?: {
    headline: string;
    key_points: string[];
    suggested_followups?: string[];
    summary_source?: string;
  };
}


function toFrontendIntent(raw: BackendPreviewIntentResponse): ParseIntentResult {
  return {
    sourceName: raw.source_name,
    sourceKind: raw.source_kind,
    mimeType: raw.mime_type,
    pipeline: raw.pipeline || [],
    contentType: raw.content_type,
    fingerprint: raw.fingerprint,
    intent: raw.intent,
    extractedTextExcerpt: raw.extracted_text_excerpt,
    extractedTextLength: raw.extracted_text_length,
    docId: raw.doc_id ?? null,
    aiSummary: raw.ai_summary,
  };
}


/** 第一步：解析 + 文档指纹 + 意图判定（不调 LLM 抽取）。
 *  本地 demo 模式下若后端不可达，降级为前端复刻的规则版意图识别。 */
export async function parseSourceIntent(input: ParsePreviewInput): Promise<ParseIntentResult> {
  const localFallback = async (): Promise<ParseIntentResult> => {
    const text = await readLocalInputText(input);
    const verdict = classifyLocalIntent(text);
    const sourceName =
      input.kind === 'file' ? input.sourceName || input.file?.name || '本地文件' : input.sourceName || '粘贴文本';
    return {
      sourceName,
      sourceKind: input.kind,
      mimeType: input.kind === 'file' ? input.file?.type || null : 'text/plain',
      pipeline: ['local_demo_intent'],
      contentType: 'normalized_text',
      fingerprint: {
        char_count: text.length,
        table_count: 0,
        paragraph_count: 0,
        price_signal_count: 0,
        spec_signal_count: 0,
        competitor_mentions: [],
        region_mentions: [],
        quarter_mentions: [],
        has_price_table: false,
        dominant_block_type: 'paragraph',
      },
      intent: verdict,
      extractedTextExcerpt: text.slice(0, 1200),
      extractedTextLength: text.length,
    };
  };

  try {
    if (input.kind === 'file') {
      if (!input.file) throw new Error('请选择要解析的文件。');
      const form = new FormData();
      form.append('file', input.file, input.file.name);
      if (input.sourceName) form.append('source_name', input.sourceName);
      const raw = await priceIntelMultipartRequest<BackendPreviewIntentResponse>(
        '/api/v1/price_intel/parse/preview-intent-file',
        form,
        { skipAuth: true },
      );
      return toFrontendIntent(raw);
    }

    const raw = await priceIntelRequest<BackendPreviewIntentResponse>(
      '/api/v1/price_intel/parse/preview-intent-text',
      {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({
          source_name: input.sourceName || '粘贴文本',
          input_text: input.inputText || '',
        }),
      },
    );
    return toFrontendIntent(raw);
  } catch (error) {
    if (isLocalDemoMode()) {
      return localFallback();
    }
    throw error;
  }
}

/** 第二步：用户确认意图后，按 intent 定向抽取，返回与一步式一致的预览结构。
 *  后端无新端点 / 调用失败时降级为旧一步式 parseSourcePreview（等价旧流程）。 */
export async function parseSourceExtract(
  input: ParsePreviewInput,
  intent: DocIntent,
  intentSource: 'rule' | 'llm' | 'user_override' = 'user_override',
): Promise<ParsePreviewResult> {
  try {
    if (input.kind === 'file') {
      if (!input.file) throw new Error('请选择要解析的文件。');
      const form = new FormData();
      form.append('file', input.file, input.file.name);
      if (input.sourceName) form.append('source_name', input.sourceName);
      form.append('intent', intent);
      form.append('intent_source', intentSource);
      const raw = await priceIntelMultipartRequest<BackendPreviewResponse>(
        '/api/v1/price_intel/parse/preview-extract-file',
        form,
        { skipAuth: true },
      );
      return toFrontendPreview(raw, {
        file: input.file,
        metadata: { ...(input.metadata || {}), intent, intent_source: intentSource },
      });
    }

    const raw = await priceIntelRequest<BackendPreviewResponse>(
      '/api/v1/price_intel/parse/preview-extract-text',
      {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({
          source_name: input.sourceName || '粘贴文本',
          input_text: input.inputText || '',
          intent,
          intent_source: intentSource,
        }),
      },
    );
    return toFrontendPreview(raw, {
      inputText: input.inputText,
      metadata: { ...(input.metadata || {}), intent, intent_source: intentSource },
    });
  } catch (error) {
    // 静默降级：等价旧一步式，仍把 intent 透传到 metadata 便于 commit 落库
    const legacy = await parseSourcePreview(input);
    return {
      ...legacy,
      _upload: {
        ...(legacy._upload || {}),
        metadata: { ...(legacy._upload?.metadata || {}), intent, intent_source: intentSource },
      },
    };
  }
}

/** 后端 PreviewResponse 的原始结构，字段名保持 snake_case 方便直接 JSON.parse。 */
interface BackendPreviewResponse {
  source_name: string;
  source_kind: 'file' | 'text';
  mime_type: string | null;
  pipeline: string[];
  content_type: string;
  extracted_text_excerpt: string;
  extracted_text_length: number;
  model: string | null;
  log: string[];
  candidates: ParsePreviewCandidate[];
}

function toFrontendPreview(
  raw: BackendPreviewResponse,
  upload: ParsePreviewResult['_upload'],
): ParsePreviewResult {
  return {
    sourceName: raw.source_name,
    sourceKind: raw.source_kind,
    mimeType: raw.mime_type,
    pipeline: raw.pipeline || [],
    contentType: raw.content_type,
    extractedTextExcerpt: raw.extracted_text_excerpt,
    extractedTextLength: raw.extracted_text_length,
    model: raw.model,
    log: raw.log || [],
    candidates: raw.candidates || [],
    _upload: upload,
  };
}

export async function parseSourcePreview(input: ParsePreviewInput): Promise<ParsePreviewResult> {
  if (input.kind === 'file') {
    if (!input.file) throw new Error('请选择要解析的文件。');
    const form = new FormData();
    form.append('file', input.file, input.file.name);
    if (input.sourceName) form.append('source_name', input.sourceName);

    const raw = await priceIntelMultipartRequest<BackendPreviewResponse>(
      '/api/v1/price_intel/parse/preview-file',
      form,
      { skipAuth: true },
    );
    return toFrontendPreview(raw, {
      file: input.file,
      metadata: input.metadata,
    });
  }

  const raw = await priceIntelRequest<BackendPreviewResponse>(
    '/api/v1/price_intel/parse/preview-text',
    {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({
        source_name: input.sourceName || '粘贴文本',
        input_text: input.inputText || '',
      }),
    },
  );
  return toFrontendPreview(raw, {
    inputText: input.inputText,
    metadata: input.metadata,
  });
}

export async function commitParsedPreview(
  preview: ParsePreviewResult,
  candidate: ParsePreviewCandidate,
): Promise<{ candidateId: string; sourceRecordId: string; ingestionJobId: string }> {
  if (getPriceIntelRuntimeMode() === 'local') {
    const committed = await commitLocalParsedPreview({
      preview: {
        source_record: {
          id: `local-source-${Date.now()}`,
          source_kind: preview.sourceKind,
          source_name: preview.sourceName,
          mime_type: preview.mimeType || undefined,
          input_text: preview._upload?.inputText,
          metadata: {
            ...(preview._upload?.metadata || {}),
            intake: 'parse_preview',
            preview_excerpt: preview.extractedTextExcerpt.slice(0, 1000),
          },
          created_by: 'demo_user',
          parse_requested_by: 'demo_user',
          parse_requested_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        pipeline: preview.pipeline,
        candidates: [
          {
            id: `local-candidate-${Date.now()}`,
            source_record_id: '', // 会被 commitLocalParsedPreview 覆盖
            ingestion_job_id: '',
            competitor: candidate.competitor ?? null,
            series: candidate.series ?? null,
            model: candidate.model ?? null,
            pitch_mm: candidate.pitch_mm ?? null,
            brightness: candidate.brightness ?? null,
            application: candidate.application ?? null,
            indoor_outdoor: candidate.indoor_outdoor ?? null,
            region: candidate.region ?? null,
            country: candidate.country ?? null,
            price_per_piece: candidate.price_per_piece ?? null,
            price_per_sqm: candidate.price_per_sqm ?? null,
            currency: candidate.currency ?? null,
            year_quarter: candidate.year_quarter ?? null,
            notes: candidate.notes ?? null,
            confidence: candidate.confidence ?? null,
            source_excerpt: candidate.source_excerpt ?? null,
            source_page: candidate.source_page ?? null,
            competitor_id: candidate.competitor_id ?? null,
            product_model_id: candidate.product_model_id ?? null,
            normalized_payload: {
              ...(candidate.normalized_payload || {}),
              origin: 'parse_preview',
              user_edited: true,
            },
            review_status: 'pending_review',
            reviewer_id: null,
            reviewed_at: null,
            review_comment: null,
            approved_price_fact_id: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as PriceIntelCandidateFact,
        ],
      },
    });
    return {
      candidateId: committed.candidates[0].id,
      sourceRecordId: committed.source_record.id,
      ingestionJobId: committed.ingestion_job.id,
    };
  }

  // 在线模式：调后端 commit-preview（需要鉴权）
  const commitMetadata = (preview._upload?.metadata || {}) as Record<string, unknown>;
  const resp = await priceIntelRequest<{
    id: string;
    source_record_id: string;
    ingestion_job_id: string;
  }>('/api/v1/price_intel/parse/commit-preview', {
    method: 'POST',
    body: JSON.stringify({
      source_name: preview.sourceName,
      source_kind: preview.sourceKind,
      mime_type: preview.mimeType,
      input_text: preview._upload?.inputText,
      file_storage_key: preview._upload?.fileStorageKey,
      metadata: commitMetadata,
      pipeline: preview.pipeline,
      extracted_text_excerpt: preview.extractedTextExcerpt,
      intent: (commitMetadata.intent as string | undefined) ?? undefined,
      intent_source: (commitMetadata.intent_source as string | undefined) ?? undefined,
      candidate,
    }),
  });
  return {
    candidateId: resp.id,
    sourceRecordId: resp.source_record_id,
    ingestionJobId: resp.ingestion_job_id,
  };
}

export interface DocQACitation {
  type?: 'text' | 'table_row' | 'image';
  index?: number;
  chunk_id?: string;
  page_number?: number | null;
  row_key?: string;
  /** 后端提取的单行定位串，用于在渲染视图中精确查找 */
  anchor?: string;
  /** 多个定位候选（段落/表格单元格），前端按序匹配 */
  anchors?: string[];
  /** 引用片段在原文中的精确字符偏移（推真实页码用，避免同名锚点跳错页） */
  text_offset?: number | null;
  image_id?: string;
  media_filename?: string;
  caption?: string;
  excerpt: string;
  relevance_score?: number;
}

export interface DocQAUsage {
  mode: string;
  total_seconds: number;
  llm_seconds: number;
  llm_calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  token_source: 'api' | 'estimate';
}

export interface DocQAResponse {
  answer: string;
  citations: DocQACitation[];
  suggested_followups?: string[];
  usage?: DocQAUsage;
}

export async function askDocumentQA(
  extractedText: string,
  question: string,
  history: Array<{ role: string; content: string }> = [],
  docId?: string,
): Promise<DocQAResponse> {
  try {
    return await priceIntelRequest<DocQAResponse>('/api/v1/price_intel/parse/doc-chat', {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({
        extracted_text: extractedText,
        question,
        history,
        ...(docId ? { doc_id: docId } : {}),
      }),
    });
  } catch (error) {
    if (isLocalDemoMode()) {
      return {
        answer: `[演示模式] 已收到您针对文档提出的问题："${question}"。\n\n根据文档分析，相关数值为：Absen BNXII 1.56mm 价格区间为 USD 1553~1780，主要覆盖 Middle East 地区。 (来源：第 1 页)`,
        citations: [{ index: 1, page_number: 1, excerpt: extractedText.slice(0, 120), relevance_score: 0.9 }],
        suggested_followups: ['这份文档的核心参数有哪些？', '包含哪些竞品品牌？'],
      };
    }
    throw error;
  }
}

// =============================================================================
// 文档中枢资产库（本地 Docker PG 持久化）
// =============================================================================

export interface DocHubDocumentSummary {
  doc_id: string;
  filename: string;
  source_kind: 'file' | 'text';
  file_ext: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  content_type: string | null;
  pipeline: string[];
  fingerprint: DocFingerprint;
  intent: IntentVerdict;
  ai_summary: ParseIntentResult['aiSummary'] | null;
  extracted_text_length: number;
  has_raw_file: boolean;
  has_preview_pdf?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocHubDocumentDetail extends Omit<DocHubDocumentSummary, 'extracted_text_length'> {
  extracted_text: string;
}

const DOC_HUB_API_BASE = import.meta.env.VITE_PRICE_INTEL_API_BASE_URL as string | undefined;

export async function listDocHubDocuments(): Promise<DocHubDocumentSummary[]> {
  const resp = await priceIntelRequest<{ documents: DocHubDocumentSummary[] }>(
    '/api/v1/price_intel/parse/documents',
    { method: 'GET', skipAuth: true },
  );
  return resp.documents;
}

export async function getDocHubDocument(docId: string): Promise<DocHubDocumentDetail> {
  return priceIntelRequest<DocHubDocumentDetail>(
    `/api/v1/price_intel/parse/documents/${docId}`,
    { method: 'GET', skipAuth: true },
  );
}

export function getDocHubFileUrl(docId: string): string {
  return `${DOC_HUB_API_BASE}/api/v1/price_intel/parse/documents/${docId}/file`;
}

/** PPT/PPTX/ODP 经 LibreOffice 转换后的高保真 PDF 预览 URL。 */
export function getDocHubPdfPreviewUrl(docId: string): string {
  return `${DOC_HUB_API_BASE}/api/v1/price_intel/parse/documents/${docId}/preview.pdf`;
}

/** 解析产物内联媒体（PPT 图片等）的访问 URL */
export function getDocHubMediaUrl(docId: string, filename: string): string {
  return `${DOC_HUB_API_BASE}/api/v1/price_intel/parse/documents/${docId}/media/${encodeURIComponent(filename)}`;
}

export async function deleteDocHubDocument(docId: string): Promise<void> {
  await priceIntelRequest<{ deleted: boolean }>(
    `/api/v1/price_intel/parse/documents/${docId}`,
    { method: 'DELETE', skipAuth: true },
  );
}

export interface DocHubIndexStatus {
  doc_id: string;
  index_status: 'not_started' | 'queued' | 'running' | 'ready' | 'failed' | 'cancelled';
  index_strategy: 'unknown' | 'direct' | 'standard_hybrid' | 'tabular_search' | 'on_demand';
  index_phase: string | null;
  index_progress: number;
  index_progress_message: string | null;
  index_embedded_count: number;
  index_embed_total: number;
  source_span_count: number;
  vector_chunk_count: number;
  indexed_text_length: number;
  index_error: string | null;
  index_cancel_requested: boolean;
  can_resume?: boolean;
  checkpoint_embedded_count?: number;
  checkpoint_embed_total?: number;
  index_checkpoint_text_hash?: string | null;
}

export async function getDocHubIndexStatus(docId: string): Promise<DocHubIndexStatus> {
  return priceIntelRequest<DocHubIndexStatus>(
    `/api/v1/price_intel/parse/documents/${docId}/index-status`,
    { method: 'GET', skipAuth: true },
  );
}

export async function cancelDocHubDocumentReindex(
  docId: string,
  options?: { force?: boolean },
): Promise<{
  doc_id: string;
  cancel_requested: boolean;
  message: string;
  forced?: boolean;
  index_status?: DocHubIndexStatus['index_status'];
  can_resume?: boolean;
  checkpoint_embedded_count?: number;
}> {
  const forceQuery = options?.force ? '?force=true' : '';
  return priceIntelRequest(
    `/api/v1/price_intel/parse/documents/${docId}/reindex/cancel${forceQuery}`,
    { method: 'POST', skipAuth: true },
  );
}

export interface DocHubReindexResult {
  doc_id: string;
  reindex_queued: boolean;
  uses_media_enriched_text: boolean;
  index_strategy: 'direct' | 'standard_hybrid' | 'tabular_search' | 'on_demand';
  resumed?: boolean;
  checkpoint_embedded_count?: number;
  checkpoint_embed_total?: number;
  force?: boolean;
}

/** 手动触发向量库重建索引（用最新的 media_enriched_text，含图片理解说明）。 */
export async function reindexDocHubDocument(
  docId: string,
  options?: { force?: boolean },
): Promise<DocHubReindexResult> {
  const forceQuery = options?.force ? '?force=true' : '';
  return priceIntelRequest<DocHubReindexResult>(
    `/api/v1/price_intel/parse/documents/${docId}/reindex${forceQuery}`,
    { method: 'POST', skipAuth: true },
  );
}

// =============================================================================
// 知识库面板：切片查看 + 召回测试
// =============================================================================

export interface DocHubChunkInfo {
  seq: number;
  preview: string;
  char_count: number;
  is_summary_node: boolean;
  raptor_level: number | null;
  has_image_insight: boolean;
  page_number: number | null;
  doc_hash: string | null;
  span_id?: string | null;
  section_path?: string;
  block_type?: string;
  char_start?: number | null;
  char_end?: number | null;
  chunk_kind?: 'vector' | 'source_span';
}

export interface DocHubChunksResult {
  doc_id: string;
  chunk_count: number;
  returned_chunk_count: number;
  chunks: DocHubChunkInfo[];
  chunk_view_type: 'vector' | 'source_span';
  has_media_enriched_text: boolean;
  text_length: number;
  indexed_text_length: number;
  index_coverage_ratio: number;
  source_span_count: number;
  vector_chunk_count: number;
  leaf_chunk_count: number;
  summary_node_count: number;
  index_status: 'not_started' | 'queued' | 'running' | 'ready' | 'failed' | 'cancelled';
  index_strategy: 'unknown' | 'direct' | 'standard_hybrid' | 'tabular_search' | 'on_demand';
  index_error: string | null;
  qa_tier?: 'tabular' | 'direct' | 'hybrid' | 'on_demand';
  qa_tier_thresholds?: {
    direct_limit: number;
    on_demand_limit: number;
  };
}

export async function listDocHubDocumentChunks(docId: string): Promise<DocHubChunksResult> {
  return priceIntelRequest<DocHubChunksResult>(
    `/api/v1/price_intel/parse/documents/${docId}/chunks`,
    { method: 'GET', skipAuth: true },
  );
}

export interface DocHubRetrievalHit {
  rank: number;
  score: number | null;
  rerank_score: number | null;
  reranked: boolean;
  preview: string;
  char_count: number;
  has_image_insight: boolean;
  page_number: number | null;
  section_path?: string;
  span_id?: string | null;
}

export interface DocHubRetrievalTestResult {
  doc_id: string;
  query: string;
  retrieval_mode: 'pgvector_hybrid' | 'on_demand_sparse' | 'direct_full_document';
  index_strategy: 'direct' | 'standard_hybrid' | 'tabular_search' | 'on_demand';
  doc_text_length: number;
  hit_count: number;
  hits: DocHubRetrievalHit[];
}

/** 召回测试：用指定问题试跑与问答一致的检索链路，不调 LLM 生成。 */
export async function testDocHubDocumentRetrieval(
  docId: string,
  query: string,
  topK = 10,
): Promise<DocHubRetrievalTestResult> {
  return priceIntelRequest<DocHubRetrievalTestResult>(
    `/api/v1/price_intel/parse/documents/${docId}/retrieval-test`,
    { method: 'POST', skipAuth: true, body: JSON.stringify({ query, top_k: topK }) },
  );
}
